import { createRouter, createWebHistory } from 'vue-router'
// import store from '@/store'
import { LoadingBar } from 'view-ui-plus'
import wj from '@/plugins/wjtools'
import home from './home'

LoadingBar.config({
  color: '#00A88E'
})

const normalizeRoutePath = path => (path || '').replace(/\/+$/, '')
const isMobilePath = path => normalizeRoutePath(path).startsWith('/m/')
const getRouteScrollPosition = (to, from, savedPosition) => {
  if (savedPosition) return savedPosition
  if (to.hash) return { el: to.hash }

  const fromPath = normalizeRoutePath(from.path)
  const toPath = normalizeRoutePath(to.path)
  if (isMobilePath(fromPath) && isMobilePath(toPath)) {
    const shouldRestoreFromMemory = !!(to.meta && to.meta.keepAlive)
    if (!shouldRestoreFromMemory) {
      return { left: 0, top: 0 }
    }
    // Let MobileLayout.vue globally handle scroll restoration for mobile keep-alive routes.
    return false
  }

  return { left: 0, top: 0 }
}

const routes = [
  ...home,
  {
    // AI超级助手全页:顶层路由,不带 main 布局(真全屏;此前嵌在 main 里被
    // 顶栏+Footer 挤压,底部输入框不可见)
    path: '/ai-entry',
    name: 'aiEntry',
    component: () => import('@/views/aiEntry/FullPage.vue'),
    meta: {
      title: 'AI超级助手'
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login'),
    meta: {
      title: '登录'
    }
  },
  {
    path: '/bindWechat',
    name: 'bindWechat',
    component: () => import('@/views/bind'),
    meta: {
      title: '微信号注册'
    }
  },
  {
    // 双号合并 H5 确认页:服务号客服消息下发链接(token 一次性),微信内 OAuth 静默授权,
    // 确认后把小程序孤儿号权益并入当前网站账号(1.32 扫码桥接自动合并)
    path: '/merge/confirm',
    name: 'mergeConfirm',
    component: () => import('@/views/merge/confirm'),
    meta: {
      title: '合并会员权益'
    }
  },
  {
    path: '/ws-claim/:code',
    name: 'wsTerminalClaim',
    component: () => import('@/views/distribute/terminal/claim'),
    meta: {
      title: '分发终端接入'
    }
  },
  {
    path: '/wechatOpen/result',
    name: 'wechatOpenResult',
    component: () => import('@/views/distribute/wechatOpen/redirect'),
    meta: {
      title: '授权结果'
    }
  },
  {
    path: '/doc/pipLinePluginDoc',
    name: 'pipLinePluginDoc',
    component: () => import('@/views/doc/pipLinePluginDoc'),
    meta: {
      title: '工作流任务文档'
    }
  },
  // Mobile Routes
  {
    path: '/m',
    component: () => import('@/mobile/layout/MobileLayout.vue'),
    redirect: '/m/daily-hot',
    children: [
      // Detail Pages (Nested to keep MobileLayout alive for keep-alive)
      {
        path: 'audit-detail',
        name: 'mobileAuditDetail',
        component: () => import('@/mobile/views/AuditDetail.vue'),
        meta: { title: '审核详情', hideTabbar: true, hideHeader: true }
      },
      {
        path: 'publish-detail',
        name: 'mobilePublishDetail',
        component: () => import('@/mobile/views/PublishDetail.vue'),
        meta: { title: '内容详情', hideTabbar: true, hideHeader: true }
      },
      {
        path: 'ai-processed-detail',
        name: 'mobileAiProcessedDetail',
        component: () => import('@/mobile/views/AiProcessedDetail.vue'),
        meta: { title: 'AI处理详情', hideTabbar: true, hideHeader: true }
      },
      {
        path: 'resource-chat-detail',
        name: 'mobileResourceChatDetail',
        component: () => import('@/mobile/views/ResourceChatDetail.vue'),
        meta: { title: '资源对话详情', hideTabbar: true, hideHeader: true }
      },
      {
        path: 'ai-write',
        name: 'mobileAiWrite',
        component: () => import('@/mobile/views/AiWrite.vue'),
        meta: { title: 'AI帮写', hideTabbar: true, hideHeader: true }
      },

      // Tab Pages
      {
        path: 'monetization-ideas',
        name: 'mobileMonetizationIdeas',
        component: () => import('@/mobile/views/MonetizationIdeas.vue'),
        meta: { title: '变现思路', keepAlive: true, hideHeader: true }
      },
      {
        path: 'monetization-detail',
        name: 'mobileMonetizationDetail',
        component: () => import('@/mobile/views/MonetizationDetail.vue'),
        meta: { title: '变现思路详情', hideTabbar: true }
      },
      {
        path: 'daily-hot',
        name: 'mobileDailyHot',
        component: () => import('@/mobile/views/DailyHotList.vue'),
        meta: { title: '今日热榜', keepAlive: true, hideHeader: true }
      },
      {
        path: 'data-dashboard',
        name: 'mobileDataDashboard',
        component: () => import('@/mobile/views/DataDashboard.vue'),
        meta: { hideTabbar: true, keepAlive: true, hideHeader: true }
      },
      {
        path: 'netdisk',
        name: 'mobileNetdiskManage',
        component: () => import('@/mobile/views/NetdiskManage.vue'),
        meta: { title: '网盘管理', hideTabbar: true, keepAlive: true, hideHeader: true }
      },
      {
        path: 'quark',
        redirect: '/m/netdisk'
      },
      {
        path: 'agents',
        redirect: to => ({ path: '/m/agent', query: { ...to.query, tab: 'mine' } })
      },
      {
        path: 'agent-square',
        redirect: to => ({ path: '/m/agent', query: { ...to.query, tab: 'square' } })
      },
      {
        // 图文库容器:顶部三段切换 审核库/已处理/待发布(?tab=audit|processed|publish)
        path: 'library',
        name: 'mobileLibrary',
        component: () => import('@/mobile/views/LibraryTab.vue'),
        meta: { title: '图文库', keepAlive: true, hideHeader: true }
      },
      {
        // 工作流容器(原智能体容器):顶部两段切换 我的/广场(?tab=mine|square),默认我的
        path: 'agent',
        name: 'mobileAgent',
        component: () => import('@/mobile/views/AgentTab.vue'),
        meta: { title: '工作流', keepAlive: true, hideHeader: true }
      },
      {
        // 对话式智能体:列表(我的+广场)
        path: 'chat-agent',
        name: 'mobileChatAgentList',
        component: () => import('@/mobile/views/ChatAgentList.vue'),
        meta: { title: '智能体', keepAlive: true, hideHeader: true }
      },
      {
        // 对话式智能体:全屏对话(agentId 由路径参数传入)
        path: 'chat-agent/chat/:id',
        name: 'mobileChatAgentChat',
        component: () => import('@/mobile/views/ChatAgentChat.vue'),
        meta: { title: '智能体对话', hideTabbar: true, hideHeader: true }
      },
      {
        // AI百宝箱聚合页:宫格入口(聊天/绘画/Claude 终端)
        path: 'ai-box',
        name: 'mobileAiBox',
        component: () => import('@/mobile/views/AiBox.vue'),
        meta: { title: 'AI百宝箱', keepAlive: true, hideHeader: true }
      },
      {
        path: 'audit',
        redirect: to => ({ path: '/m/library', query: { ...to.query, tab: 'audit' } })
      },
      {
        path: 'ai-processed',
        redirect: to => ({ path: '/m/library', query: { ...to.query, tab: 'processed' } })
      },
      {
        path: 'publish',
        redirect: to => ({ path: '/m/library', query: { ...to.query, tab: 'publish' } })
      },
      {
        path: 'published',
        name: 'mobilePublished',
        component: () => import('@/mobile/views/PublishedList.vue'),
        meta: { title: '已分发图文库', keepAlive: true }
      },
      {
        path: 'account',
        name: 'mobileAccount',
        component: () => import('@/mobile/views/AccountList.vue'),
        meta: { title: '自媒体账号', keepAlive: true }
      },
      {
        path: 'favorite',
        name: 'mobileFavorite',
        component: () => import('@/mobile/views/FavoriteList.vue'),
        meta: { title: '收藏夹', keepAlive: true }
      },
      {
        path: 'more',
        name: 'mobileMore',
        component: () => import('@/mobile/views/More.vue'),
        meta: { title: '更多', keepAlive: true, hideHeader: true }
      },
      {
        path: 'cover',
        name: 'mobileCover',
        component: () => import('@/mobile/views/CoverList.vue'),
        meta: { title: '图片库', keepAlive: true }
      },
      {
        path: 'style-profile',
        name: 'mobileStyleProfile',
        component: () => import('@/mobile/views/StyleProfileList.vue'),
        meta: { title: '内容风格库', keepAlive: true }
      },
      {
        path: 'skill-package',
        name: 'mobileSkillPackage',
        component: () => import('@/mobile/views/SkillPackageList.vue'),
        meta: { title: 'Skill 库', keepAlive: true }
      },
      {
        path: 'xhs-style',
        name: 'mobileXhsStyle',
        component: () => import('@/mobile/views/XhsStyleList.vue'),
        meta: { title: '小红书样式库', keepAlive: true }
      },
      {
        path: 'content-image-library',
        name: 'mobileContentImageLibrary',
        component: () => import('@/mobile/views/ContentImageLibraryList.vue'),
        meta: { title: '内容配图库', keepAlive: true }
      },
      {
        path: 'history',
        name: 'mobileHistory',
        component: () => import('@/mobile/views/DistributeHistory.vue'),
        meta: { title: '分发历史', keepAlive: true }
      },
      {
        path: 'category',
        name: 'mobileCategory',
        component: () => import('@/mobile/views/DistributeCategory.vue'),
        meta: { title: '分类管理', keepAlive: true }
      },
      {
        path: 'api-setting',
        name: 'mobileApiSetting',
        component: () => import('@/mobile/views/ApiSetting.vue'),
        meta: { title: 'API接口', keepAlive: true, hideHeader: true }
      },
      {
        path: 'product',
        name: 'mobileDistributeProduct',
        component: () => import('@/mobile/views/DistributeProduct.vue'),
        meta: { title: '商品管理', keepAlive: true }
      },
      {
        path: 'xianyu-product',
        name: 'mobileXianyuProduct',
        component: () => import('@/mobile/views/XianyuProduct.vue'),
        meta: { title: '闲鱼商品库', keepAlive: true }
      },
      {
        path: 'xianyu-account',
        name: 'mobileXianyuAccount',
        component: () => import('@/mobile/views/XianyuAccount.vue'),
        meta: { title: '闲鱼账号' }
      },
      {
        path: 'xianyu-order',
        name: 'mobileXianyuOrder',
        component: () => import('@/mobile/views/XianyuOrder.vue'),
        meta: { title: '订单与发货' }
      },
      {
        path: 'ai-model-list',
        name: 'mobileAiModelList',
        component: () => import('@/mobile/views/AiModelList.vue'),
        meta: { title: 'AI模型', keepAlive: true, hideHeader: true }
      },
      {
        path: 'ws-terminal',
        name: 'mobileWsTerminal',
        component: () => import('@/mobile/views/WsTerminalList.vue'),
        meta: { title: '分发终端', keepAlive: true, hideHeader: true }
      },
      {
        path: 'prompt-template',
        name: 'mobilePromptTemplate',
        component: () => import('@/mobile/views/PromptTemplateList.vue'),
        meta: { title: '提示词模板', keepAlive: true, hideHeader: true }
      },
      {
        path: 'resource-chat',
        name: 'mobileResourceChat',
        component: () => import('@/mobile/views/ResourceChatList.vue'),
        meta: { title: '资源对话', keepAlive: true }
      },
      {
        path: 'text-library',
        name: 'mobileTextLibrary',
        component: () => import('@/views/content/textLibrary/index.vue'),
        meta: { title: '图文内容库', keepAlive: true }
      },
      {
        path: 'text-library-pending',
        name: 'mobileTextLibraryPending',
        component: () => import('@/views/content/textLibrary/index.vue'),
        meta: { title: 'AI待处理库', keepAlive: true }
      },
      {
        path: 'history-detail',
        name: 'mobileHistoryDetail',
        component: () => import('@/mobile/views/DistributeHistoryDetail.vue'),
        meta: { title: '分发详情', hideTabbar: true, hideHeader: true }
      },
      {
        path: 'ai-box/chat',
        name: 'mobileAiBoxChat',
        component: () => import('@/mobile/views/aiBox/Chat.vue'),
        meta: { title: 'AI 聊天', keepAlive: true, hideTabbar: true, hideHeader: true }
      },
      {
        path: 'ai-box/draw',
        name: 'mobileAiBoxDraw',
        component: () => import('@/mobile/views/aiBox/Draw.vue'),
        meta: { title: 'AI 绘画', keepAlive: true, hideTabbar: true, hideHeader: true }
      },
      {
        path: 'ai-box/claude-code',
        name: 'mobileAiBoxClaudeCode',
        component: () => import('@/mobile/views/aiBox/ClaudeCode.vue'),
        meta: { title: 'Claude 终端', keepAlive: true, hideTabbar: true, hideHeader: true }
      },
      {
        path: 'ai-entry',
        name: 'mobileAiEntry',
        component: () => import('@/mobile/views/AiEntry.vue'),
        meta: { title: 'AI 超级入口', keepAlive: true, hideTabbar: true, hideHeader: true }
      },
      {
        path: 'mcp-portal',
        name: 'mobileMcpPortal',
        component: () => import('@/mobile/views/mcpPortal/index.vue'),
        meta: { title: 'MCP 接入', keepAlive: true, hideHeader: true }
      },
      {
        path: 'loop-agent',
        name: 'mobileLoopAgent',
        component: () => import('@/mobile/views/loopAgent/index.vue'),
        meta: { title: 'Loop Agent', keepAlive: true, hideHeader: true }
      },
      {
        path: 'loop-agent/skill-draft',
        name: 'mobileLoopSkillDraftEditor',
        component: () => import('@/mobile/views/loopAgent/SkillDraftEditor.vue'),
        meta: { title: 'Skill 草稿编辑', hideTabbar: true, hideHeader: true }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('@/views/404.vue'),
    meta: { title: '页面不存在' }
  }
]

// 定义路由实例
const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return getRouteScrollPosition(to, from, savedPosition)
  }
})

router.beforeEach(async (to) => {
  console.log(wj.isLogin())
  console.log(to.path)

  // Mobile Adaptation Logic
  // Respect explicit user preference only; avoid forcing mobile by UA/width
  // so "desktop site" mode on mobile browsers can use full PC pages/plugins.
  let isMobile = false

  // Respect user preference if set
  const preferredMode = localStorage.getItem('preferredMode');
  if (preferredMode === 'mobile') {
      isMobile = true;
  } else if (preferredMode === 'pc') {
      isMobile = false;
  }

  const isMobileRoute = to.path.startsWith('/m/');

  // Allow login and public pages to pass through without redirect loop checks initially
  const publicPages = ['/login', '/bindWechat', '/merge/confirm', '/wechatOpen/result', '/forgetpwd', '/resetpwd'];
  
  if (publicPages.indexOf(to.path) === -1) {
      if (isMobile && !isMobileRoute) {
          // If we are forcing mobile but on PC route, go to mobile home
          // Ideally we would map the route, but for now home is safe
          return { path: '/m/daily-hot', replace: true }
      }
      if (!isMobile && isMobileRoute) {
          // If we are forcing PC but on mobile route, go to PC home
          return { path: '/', replace: true }
      }
  }


  // 要阻止的路由
  // let pathList = ['/home']
  // if (pathList.indexOf(to.path) !== -1) {
  //   Notice.warning({
  //     title: '提示',
  //     desc: '功能升级中，敬请期待'
  //   })
  //   return
  // }
  LoadingBar.start()
  if (to.meta.title) {
    document.title = to.meta.title
  }
  // if (to.path !== '/') {
  //   indexScrollTop = document.body.scrollTop
  // }

  // 登录权限控制
  // if (to.meta.needAuth && !wj.isLogin()) {
  //   LoadingBar.finish()
  //   let path = from.meta.needAuth ? '/' : from.path
  //   // router.replace(path)
  //   next({ replace: true, path })
  //   wj.login(() => {
  //     router.push({
  //       path: to.path,
  //       params: to.params,
  //       query: to.query
  //     })
  //   })
  // }

  // 访问权限控制：如果没有权限列表，则发请求获取
  // if (wj.isLogin() && !store.state.permission.menuList) {
  //   await store.dispatch('permission/getPermission')
  //   next({ ...to, replace: true })
  //   return
  // }


  // 访问权限控制
  // if (to.meta.access && to.meta.access !== wj.params.role) {
  //   next({ name: 'page404' })
  // }

  // 未登录跳转至未登录页
  if (!wj.isLogin() && publicPages.indexOf(to.path) === -1) {
    LoadingBar.finish()
    return '/login'
  }

  // 访问权限控制
  // if (wj.isLogin()) {
  //   const res = await api.pathList()
  //   if (+res.code === 20000) {
  //     if (res.data.indexOf(to.path) < 0) {
  //       router.push('/')
  //       return
  //     }
  //   } else {
  //     Message.error(res.msg || '获取数据失败')
  //     return
  //   }
  // }

  // 登录后禁止去未登录页
  if (wj.isLogin() && to.path === '/login') {
    return '/'
  }


  return true
})

router.afterEach(() => {
  LoadingBar.finish()

  // window.scrollTo(0, 0)
  // if (wj.isLogin()) {
  //   store.dispatch('app/getUserMsg')
  // }
})
export default router
